/**
 * Main application layout with navigation and auth context
 */

import { Link, Outlet } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { authApi, adminApi } from '../services/api'
import { LoadingSpinner } from './LoadingSpinner'
import { CookieConsent } from './CookieConsent'

export const Layout = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth()

  // Check admin status (only when authenticated)
  const { data: adminStatus } = useQuery({
    queryKey: ['admin-status'],
    queryFn: adminApi.checkAdminStatus,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  })

  const isAdmin = adminStatus?.is_admin ?? false

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <LoadingSpinner message="Loading..." size="large" />
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header
        style={{
          background: '#2c3e50',
          color: 'white',
          padding: '1rem 2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1.5rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img
            src="/images/logo_3.png"
            alt="Fact Checker Logo"
            style={{
              height: '50px',
              width: '50px',
              flexShrink: 0,
              objectFit: 'contain',
              background: 'white',
              borderRadius: '8px',
              padding: '4px',
            }}
          />
          <h1 className="brand-title" style={{ margin: 0 }}>
            <Link to="/" style={{ color: 'white', textDecoration: 'none' }}>
              Fact Checker
            </Link>
          </h1>
        </div>

        <nav style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Link to="/results" style={{ color: 'white', textDecoration: 'none' }}>
            Results
          </Link>
          <Link to="/upload" style={{ color: 'white', textDecoration: 'none' }}>
            Verify Claims
          </Link>
          <Link to="/mcp-setup" style={{ color: 'white', textDecoration: 'none' }}>
            MCP Setup
          </Link>
          {isAdmin && (
            <Link
              to="/admin"
              style={{
                color: '#f1c40f',
                textDecoration: 'none',
                fontWeight: 'bold',
              }}
            >
              Admin
            </Link>
          )}
          {isAuthenticated ? (
            <>
              <span style={{ color: '#ecf0f1' }}>{user?.email}</span>
              <button
                onClick={() => logout()}
                style={{
                  background: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={() => authApi.login()}
              style={{
                background: '#3498db',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Sign In
            </button>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main style={{ flex: 1, padding: '2rem' }}>
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        style={{
          background: '#34495e',
          color: '#ecf0f1',
          padding: '1rem 2rem',
          textAlign: 'center',
        }}
      >
        <p style={{ margin: '0 0 0.5rem 0' }}>
          Source verification tool - AI-assisted with human review
        </p>
        <div
          style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', fontSize: '0.85rem' }}
        >
          <Link to="/methodology" style={{ color: '#bdc3c7', textDecoration: 'none' }}>
            How it works
          </Link>
          <Link to="/terms" style={{ color: '#bdc3c7', textDecoration: 'none' }}>
            Terms
          </Link>
          <Link to="/feedback" style={{ color: '#bdc3c7', textDecoration: 'none' }}>
            Feedback
          </Link>
        </div>
      </footer>

      {/* Cookie consent banner - shows at bottom until accepted/rejected */}
      <CookieConsent />
    </div>
  )
}
